/*
PURPOSE: Dedicated login page for users redirected from protected routes
USE CASES:
- Clean, focused login experience when accessing protected routes while unauthenticated
- Preserves the intended destination URL for post-login redirect
DOMAIN BOUNDARIES:
- Handles user authentication via magic link or password
- Redirects to stored destination URL after successful login
- Presentation is the editorial design system (warm ivory ground, Cormorant
  display heading, editorial accent), consistent with the migrated public pages.
  No coral-reef illustration, no rainbow coral-icon.
ASSUMPTIONS:
- Login redirect URL is stored in sessionStorage before redirecting here
- Uses same auth mechanisms as AuthModal (magic link preferred, password fallback)
*/
import React, { useState, lazy, Suspense, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CoralButton from '../../../components/CoralButtons/CoralButton';
import { DisplayHeading, EDITORIAL_CTA_OVERRIDE, EDITORIAL_INPUT } from '../../../components/editorial';
import EditorialAuthShell from '../../../components/EditorialAuthShell/EditorialAuthShell';
import { requestMagicLink } from '../../../api/api';
import { AuthContext } from '../../../components/Auth/AuthContext/AuthContext';
import { getAndClearLoginRedirectUrl } from '../../../utils/authUtils';

const LazyAuthenticator = lazy(() => import('@aws-amplify/ui-react').then(module => ({
  default: module.Authenticator
})));

const Login: React.FC = () => {
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  // Which view the Amplify Authenticator opens on when the password path mounts.
  // 'forgotPassword' lets us surface a first-class reset entry point from the
  // magic-link default view without the user first toggling to "Use a password".
  const [authInitialState, setAuthInitialState] = useState<'signIn' | 'forgotPassword'>('signIn');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [message, setMessage] = useState('');
  const { fetchUserData } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHandledRef = useRef(false);

  const handleToggle = () => {
    setUsePasswordLogin(!usePasswordLogin);
    setAuthInitialState('signIn');
    setMessage('');
    setIsEmailSent(false);
  };

  const handleForgotPassword = () => {
    setAuthInitialState('forgotPassword');
    setUsePasswordLogin(true);
    setMessage('');
    setIsEmailSent(false);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      await requestMagicLink(email);
      setIsEmailSent(true);
      setMessage('Sign-in link sent! Please check your email.');
    } catch (error) {
      setMessage('Error sending sign-in link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = async () => {
    await fetchUserData();
    const redirectUrl = getAndClearLoginRedirectUrl() || '/dashboard';
    navigate(redirectUrl, { replace: true });
  };

  const AuthComponents = {
    Header() {
      return null;
    },
  };

  return (
    <EditorialAuthShell bloomSeed={4}>
      <DisplayHeading as="h1" scale="heading" measure={false} className="text-center">
        Welcome back
      </DisplayHeading>
      <p className="mt-4 text-editorial-body text-editorial-muted/70">
        Sign in to continue to the community
      </p>

      {!usePasswordLogin ? (
        <div className="mt-10">
          {!isEmailSent ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={EDITORIAL_INPUT}
                placeholder="Enter your email"
                required
                autoFocus
              />
              <CoralButton
                onClick={() => {}}
                className={`w-full ${EDITORIAL_CTA_OVERRIDE}`}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Continue with Email'}
              </CoralButton>
              {message && <p className="text-sm text-editorial-muted/70">{message}</p>}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-editorial-muted/60 hover:text-editorial-muted underline"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <p className="font-cormorant text-editorial-muted text-2xl md:text-3xl leading-tight">
                Check your email
              </p>
              <p className="mt-3 text-sm text-editorial-muted/70">{message}</p>
              <p className="mt-4 text-sm text-editorial-muted/70">
                Didn&apos;t receive the email?
                <button
                  onClick={() => setIsEmailSent(false)}
                  className="ml-1 text-editorial-accent hover:text-editorial-accent/80 underline"
                >
                  Try again
                </button>
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-10 text-left">
          <Suspense fallback={<div className="text-center py-4 text-editorial-muted/60">Loading...</div>}>
            <LazyAuthenticator
              initialState={authInitialState}
              hideSignUp={true}
              components={AuthComponents}
              loginMechanisms={['email']}
            >
              {({ user }) => {
                if (user && !authHandledRef.current) {
                  authHandledRef.current = true;
                  handleAuthSuccess();
                }
                return (<></>);
              }}
            </LazyAuthenticator>
          </Suspense>
        </div>
      )}

      <button
        onClick={handleToggle}
        className="mt-6 text-sm text-editorial-muted/60 hover:text-editorial-muted underline"
      >
        {usePasswordLogin ? 'Use passwordless login' : 'Use a password'}
      </button>
    </EditorialAuthShell>
  );
};

export default Login;
